import { REPORT_BLOCKS, BLOCK_CATEGORIES, type BlockType } from "@/lib/reports/block-types";
import { Card } from "@/components/ui/card";
import { Plus } from "lucide-react";

interface BlockPaletteProps {
  canvasBlocks: Array<{ type: BlockType }>;
  onAddBlock: (type: BlockType) => void;
}

function PaletteBlock({ blockType, disabled, onAdd }: { blockType: BlockType; disabled: boolean; onAdd: () => void }) {
  const definition = REPORT_BLOCKS[blockType];
  const Icon = definition.icon;

  return (
    <Card
      className={`flex items-center gap-2 p-2.5 transition-colors ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:shadow-sm hover:border-primary/50"}`}
      onClick={disabled ? undefined : onAdd}
    >
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-tight truncate">{definition.name}</p>
        <p className="text-[10px] text-muted-foreground truncate">{definition.description}</p>
      </div>
      {!disabled && <Plus className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
    </Card>
  );
}

export function BlockPalette({ canvasBlocks, onAddBlock }: BlockPaletteProps) {
  const blockCountByType = canvasBlocks.reduce<Record<string, number>>((acc, b) => {
    acc[b.type] = (acc[b.type] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="w-[240px] border-r overflow-y-auto p-3 space-y-4 shrink-0">
      <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">Blocks</h3>
      {BLOCK_CATEGORIES.map((category) => {
        const blocks = Object.entries(REPORT_BLOCKS).filter(([, def]) => def.category === category.key);
        if (blocks.length === 0) return null;
        return (
          <div key={category.key} className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground px-1">{category.label}</p>
            {blocks.map(([type, def]) => {
              const count = blockCountByType[type] || 0;
              const atMax = count >= def.maxInstances;
              return (
                <PaletteBlock
                  key={type}
                  blockType={type as BlockType}
                  disabled={atMax}
                  onAdd={() => onAddBlock(type as BlockType)}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
